import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchAvailableItems } from '../../store/slices/inventorySlice';
import { Package, Search, Filter, Loader2, AlertCircle } from 'lucide-react';

interface AvailableItemsProps {
  onItemSelect?: (item: any) => void;
}

const AvailableItems: React.FC<AvailableItemsProps> = ({ onItemSelect }) => {
  const dispatch = useAppDispatch();
  const { 
    availableItems, 
    availableItemsLoading, 
    availableItemsError,
    availableItemsPagination 
  } = useAppSelector((state) => state.inventory);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  useEffect(() => {
    // Load initial data
    dispatch(fetchAvailableItems({}));
  }, [dispatch]);

  const handleSearch = () => {
    const params: any = {};
    if (searchTerm.trim()) params.search = searchTerm;
    if (selectedColor) params.color = selectedColor;
    if (selectedSize) params.size = selectedSize;
    
    dispatch(fetchAvailableItems(params));
  };

  const handlePageChange = (page: number) => {
    const params: any = { page };
    if (searchTerm.trim()) params.search = searchTerm;
    if (selectedColor) params.color = selectedColor;
    if (selectedSize) params.size = selectedSize;
    
    dispatch(fetchAvailableItems(params));
  };

  if (availableItemsError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Available Items</h3>
              <p className="text-sm text-red-700 mt-1">{availableItemsError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Available Items</h3>
        <p className="text-gray-600">Items available for stock approval</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by style code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
            
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <select
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Colors</option>
                  <option value="Black">Black</option>
                  <option value="White">White</option>
                  <option value="Blue">Blue</option>
                  <option value="Red">Red</option>
                  <option value="Green">Green</option>
                  <option value="Beige">Beige</option>
                  <option value="Khaki">Khaki</option>
                  <option value="Wine">Wine</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All Sizes</option>
                  <option value="UK 6">UK 6</option>
                  <option value="UK 8">UK 8</option>
                  <option value="UK 10">UK 10</option>
                  <option value="UK 12">UK 12</option>
                  <option value="UK 14">UK 14</option>
                  <option value="UK 16">UK 16</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {availableItemsLoading && (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Loading available items...</p>
        </div>
      )}

      {/* Results */}
      {!availableItemsLoading && availableItems.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-md font-medium text-gray-900">
              Available Items ({availableItemsPagination.total_records} total)
            </h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableItems.map((item, index) => (
              <div 
                key={index} 
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onItemSelect && onItemSelect(item)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{item.style_code}</h5>
                    <p className="text-sm text-gray-600">{item.style_name}</p>
                    {item.is_jeans && (
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded mt-1">
                        Jeans
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <Package className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-gray-500">Available Sizes:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.available_sizes.map((size, sizeIndex) => (
                        <span 
                          key={sizeIndex}
                          className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                        >
                          {size}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-xs font-medium text-gray-500">Available Colors:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.available_colors.map((color, colorIndex) => (
                        <span 
                          key={colorIndex}
                          className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                        >
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      {item.variant_count} variants
                    </span>
                    {item.current_stock !== undefined && (
                      <span className="text-xs font-medium text-gray-700">
                        Stock: {item.current_stock}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {availableItemsPagination.total_pages > 1 && (
            <div className="flex justify-center items-center space-x-4 pt-4">
              <button
                onClick={() => handlePageChange(availableItemsPagination.current_page - 1)}
                disabled={availableItemsPagination.current_page === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-700">
                Page {availableItemsPagination.current_page} of {availableItemsPagination.total_pages}
              </span>
              
              <button
                onClick={() => handlePageChange(availableItemsPagination.current_page + 1)}
                disabled={availableItemsPagination.current_page === availableItemsPagination.total_pages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Results */}
      {!availableItemsLoading && availableItems.length === 0 && (
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Available Items Found</h3>
          <p className="text-gray-600">
            {searchTerm || selectedColor || selectedSize 
              ? 'Try adjusting your search criteria or filters.'
              : 'No items are currently available for stock approval.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default AvailableItems;
